import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, Loader2, Ticket, ListTodo, Activity, Mail, Phone, Building2, ArrowLeft, ChevronRight, Clock, Plus } from "lucide-react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";
import { ClientCreateImport } from "../components/ClientCreateImport";

const TST = { open: ["Ouvert", "#3B82F6"], answered: ["Répondu", "#10B981"], converted: ["Converti", "#8B5CF6"], closed: ["Fermé", "#71717a"], todo: ["À faire", "#71717a"], in_progress: ["En cours", "#F59E0B"], review: ["Validation", "#8B5CF6"], done: ["Terminé", "#10B981"], active: ["Actif", "#10B981"], paused: ["En pause", "#F59E0B"], pending: ["En attente", "#3B82F6"] };
const fmtH = (s) => { const h = Math.floor((s || 0) / 3600), m = Math.floor(((s || 0) % 3600) / 60); return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m}min`; };
function Badge({ s }) { const [l, c] = TST[s] || [s, "#71717a"]; return <span className="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0" style={{ background: c + "22", color: c }}>{l}</span>; }

export default function ClientsPage() {
  const { selectedId } = useCompany();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [dLoading, setDLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const cq = selectedId && selectedId !== "all" ? `?company_id=${selectedId}` : ""; const { data } = await api.get(`/clients${cq}`); setClients(data || []); }
    catch { setClients([]); } finally { setLoading(false); }
  }, [selectedId]);
  useEffect(() => { load(); setActive(null); setDetail(null); }, [load]);

  const open = async (c) => {
    navigate(`/clients/${encodeURIComponent(c.email)}`);
  };

  const filtered = clients.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.email.toLowerCase().includes(q.toLowerCase()) || (c.company || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="h-full flex flex-col" data-testid="clients-page">
      <div className="h-14 px-4 flex items-center gap-3 border-b border-white/10 shrink-0">
        <Users size={18} className="text-klein" />
        <h1 className="font-cabinet font-bold text-lg">Clients</h1>
        <span className="text-xs text-zinc-500 tabular-nums">{clients.length}</span>
        <div className="ml-auto"><ClientCreateImport onDone={load} /></div>
      </div>
      <div className="flex-1 flex min-h-0">
        {/* List */}
        <div className={`${active ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[340px] border-r border-white/10 shrink-0`}>
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center gap-2 bg-background/50 border border-white/10 rounded-lg px-3 h-9">
              <Search size={14} className="text-zinc-500" />
              <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="flex-1 bg-transparent text-sm focus:outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <div className="p-8 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
              : filtered.length === 0 ? <div className="p-12 text-center text-zinc-400 text-sm">Aucun client.</div>
              : filtered.map((c) => (
                <button key={c.email} data-testid={`client-${c.email}`} onClick={() => open(c)} className={`w-full text-left px-4 py-3 border-b border-white/[0.05] hover:bg-background transition-colors flex items-center gap-3 ${active?.email === c.email ? "bg-background" : ""}`}>
                  <Avatar name={c.name} src={c.avatar_url} color="#0EA5E9" size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.company || c.email}</div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-400">
                      <span className="flex items-center gap-0.5"><Ticket size={10} />{c.tickets}</span>
                      <span className="flex items-center gap-0.5"><ListTodo size={10} />{c.tasks}</span>
                      <span className="flex items-center gap-0.5"><Activity size={10} />{c.services}</span>
                      {c.open_tickets > 0 && <span className="text-klein font-semibold">{c.open_tickets} ouvert(s)</span>}
                    </div>
                  </div>
                  <ChevronRight size={15} className="text-zinc-500 shrink-0" />
                </button>
              ))}
          </div>
        </div>

        {/* Detail 360° */}
        <div className={`${active ? "flex" : "hidden lg:flex"} flex-col flex-1 min-w-0`}>
          {!active ? <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">Sélectionnez un client pour voir sa fiche.</div>
            : dLoading || !detail ? <div className="flex-1 flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
            : (
              <div className="flex-1 overflow-y-auto">
                <div className="p-5 border-b border-white/10 flex items-center gap-4">
                  <button data-testid="client-back" onClick={() => { setActive(null); setDetail(null); }} className="lg:hidden p-1.5 rounded-lg hover:bg-white/[0.06]"><ArrowLeft size={18} /></button>
                  <Avatar name={detail.name} src={detail.contact?.avatar_url} color="#0EA5E9" size={56} />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-cabinet font-bold text-xl truncate">{detail.name}</h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-400">
                      <a href={`mailto:${detail.email}`} className="flex items-center gap-1 hover:text-klein"><Mail size={12} />{detail.email}</a>
                      {detail.contact?.phone && <a href={`tel:${detail.contact.phone}`} className="flex items-center gap-1 hover:text-klein"><Phone size={12} />{detail.contact.phone}</a>}
                      {detail.contact?.company && <span className="flex items-center gap-1"><Building2 size={12} />{detail.contact.company}</span>}
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col gap-2 shrink-0">
                    <button data-testid="client-new-ticket" onClick={() => navigate("/tickets", { state: { prefillClient: { email: detail.email, name: detail.name, phone: detail.contact?.phone } } })} className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-klein/15 text-klein text-xs font-semibold hover:bg-klein/25 transition"><Plus size={13} /> Ticket</button>
                    <button data-testid="client-new-task" onClick={() => navigate("/tasks", { state: { prefillClient: { email: detail.email, name: detail.name, phone: detail.contact?.phone } } })} className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold hover:bg-emerald-500/25 transition"><Plus size={13} /> Tâche</button>
                  </div>
                </div>

                {/* Mini-résumé 360 */}
                <div className="px-5 pt-4 grid grid-cols-4 gap-2.5" data-testid="client-summary">
                  <Stat label="Tickets" value={detail.tickets.length} sub={`${detail.tickets.filter((t) => ["open", "answered"].includes(t.status)).length} ouvert(s)`} color="#3B82F6" />
                  <Stat label="Tâches" value={detail.tasks.length} color="#10B981" />
                  <Stat label="Services" value={detail.services.length} color="#06B6D4" />
                  <Stat label="Temps passé" value={fmtH(detail.tasks.reduce((a, t) => a + (t.time_spent_seconds || 0), 0))} color="#F59E0B" />
                </div>

                <div className="p-5 space-y-6">
                  <Section title="Tickets" icon={Ticket} color="#3B82F6" count={detail.tickets.length} onAll={() => navigate("/tickets")}>
                    {detail.tickets.map((t) => (
                      <Row key={t.id} testid={`client-ticket-${t.id}`} onClick={() => navigate("/tickets")}>
                        <span className="font-mono-label text-[10px] text-zinc-500 shrink-0">{t.ref}</span>
                        <span className="flex-1 truncate text-sm">{t.subject}</span>
                        {t.company_name && <span className="text-[10px] text-zinc-500 truncate hidden sm:inline">{t.company_name}</span>}
                        <Badge s={t.status} />
                      </Row>
                    ))}
                  </Section>
                  <Section title="Tâches" icon={ListTodo} color="#10B981" count={detail.tasks.length} onAll={() => navigate("/tasks")}>
                    {detail.tasks.map((t) => (
                      <Row key={t.id} testid={`client-task-${t.id}`} onClick={() => navigate("/tasks")}>
                        <span className="font-mono-label text-[10px] text-zinc-500 shrink-0">{t.ref}</span>
                        <span className="flex-1 truncate text-sm">{t.title}</span>
                        {t.time_spent_seconds > 0 && <span className="text-[10px] text-zinc-400 flex items-center gap-0.5 shrink-0"><Clock size={9} />{fmtH(t.time_spent_seconds)}</span>}
                        <Badge s={t.status} />
                      </Row>
                    ))}
                  </Section>
                  <Section title="Services / Suivi" icon={Activity} color="#06B6D4" count={detail.services.length} onAll={() => navigate("/suivi")}>
                    {detail.services.map((s) => (
                      <Row key={s.id} testid={`client-service-${s.id}`} onClick={() => navigate("/suivi")}>
                        <span className="flex-1 truncate text-sm">{s.name}</span>
                        {s.company_name && <span className="text-[10px] text-zinc-500 truncate hidden sm:inline">{s.company_name}</span>}
                        <Badge s={s.status} />
                      </Row>
                    ))}
                  </Section>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, color, count, onAll, children }) {
  return (
    <div data-testid={`client-section-${title}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} style={{ color }} />
        <h3 className="font-cabinet font-bold text-sm">{title}</h3>
        <span className="text-xs text-zinc-500 tabular-nums">{count}</span>
        <div className="flex-1 h-px bg-white/[0.06]" />
        {count > 0 && <button onClick={onAll} className="text-[11px] text-klein hover:underline">Ouvrir le module</button>}
      </div>
      {count === 0 ? <p className="text-xs text-zinc-500 pl-1">Aucun élément.</p> : <div className="space-y-1.5">{children}</div>}
    </div>
  );
}
function Row({ testid, onClick, children }) {
  return <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-2.5 rounded-lg bg-background/40 border border-white/10 px-3 py-2.5 hover:border-klein/40 transition-colors text-left">{children}</button>;
}
function Stat({ label, value, sub, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-background/40 px-3 py-2.5">
      <div className="font-cabinet font-black text-lg tabular-nums leading-none" style={{ color }}>{value}</div>
      <div className="text-[11px] text-zinc-400 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
    </div>
  );
}
